import React, { useState, useEffect } from 'react';
import { Card, Button, Badge, Row, Col, Spinner, Alert, Form, Modal, Image } from 'react-bootstrap';
import { FaHeart, FaComment, FaEye, FaFlag, FaReply, FaArrowLeft, FaThumbsUp } from 'react-icons/fa';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../../api/apiClient';
import { getAssetUrl } from '../../../utils/apiBase';
import { useAuth } from '../../../contexts/AuthContext';
import { decodeForumId } from '../../../utils/urlEncryption';

const UserForumDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [forum, setForum] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [commentForm, setCommentForm] = useState({
    content: '',
    attachment: null
  });
  const [submitting, setSubmitting] = useState(false);
  const [likedForums, setLikedForums] = useState(new Set());
  const [likedComments, setLikedComments] = useState(new Set());

  useEffect(() => {
    fetchForum();
    fetchComments();
  }, [id]);

  const fetchForum = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let decodedId = decodeForumId(id);
      if (!decodedId) {
        const numericId = parseInt(id);
        if (!isNaN(numericId)) {
          decodedId = numericId;
        } else {
          setError('Invalid forum ID');
          return;
        }
      }
      
      const response = await api.forums.getById(decodedId);
      
      if (response.data.success) {
        setForum(response.data.data);
      } else {
        setError(response.data.message || 'Failed to fetch forum');
      }
    } catch (error) {
      console.error('Error fetching forum:', error);
      setError('Failed to fetch forum. Please try again.');
      toast.error('Failed to fetch forum');
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      setCommentsLoading(true);
      
      let decodedId = decodeForumId(id);
      if (!decodedId) {
        const numericId = parseInt(id);
        if (!isNaN(numericId)) {
          decodedId = numericId;
        } else {
          toast.error('Invalid forum ID');
          return;
        }
      }
      
      const response = await api.forums.getComments(decodedId, {
        page: currentPage,
        limit: 20
      });
      
      if (response.data.success) {
        setComments(response.data.data.comments);
        setTotalPages(response.data.data.totalPages);
      } else {
        toast.error(response.data.message || 'Failed to fetch comments');
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
      toast.error('Failed to fetch comments');
    } finally {
      setCommentsLoading(false);
    }
  };

  const handleLikeForum = async () => {
    try {
      const decodedId = decodeForumId(id);
      if (!decodedId) return;
      
      const response = await api.forums.toggleLike(decodedId);
      
      if (response.data.success) {
        setForum(prev => ({
          ...prev,
          likes_count: response.data.likesCount
        }));
        
        if (response.data.liked) {
          setLikedForums(prev => new Set([...prev, id]));
        } else {
          setLikedForums(prev => {
            const newSet = new Set(prev);
            newSet.delete(id);
            return newSet;
          });
        }
      } else {
        toast.error(response.data.message || 'Failed to like forum');
      }
    } catch (error) {
      console.error('Error liking forum:', error);
      toast.error('Failed to like forum');
    }
  };

  const handleLikeComment = async (commentId) => {
    try {
      const response = await api.forums.toggleCommentLike(commentId);
      
      if (response.data.success) {
        setComments(prev => prev.map(comment => 
          comment.id === commentId 
            ? { ...comment, likes_count: response.data.likesCount }
            : comment
        ));
        
        if (response.data.liked) {
          setLikedComments(prev => new Set([...prev, commentId]));
        } else {
          setLikedComments(prev => {
            const newSet = new Set(prev);
            newSet.delete(commentId);
            return newSet;
          });
        }
      } else {
        toast.error(response.data.message || 'Failed to like comment');
      }
    } catch (error) {
      console.error('Error liking comment:', error);
      toast.error('Failed to like comment');
    }
  };

  const handleHighlightComment = async (commentId) => {
    if (!window.confirm('Are you sure you want to mark this comment as vulgar?')) {
      return;
    }

    try {
      const response = await api.forums.highlightComment(commentId, 'Marked as vulgar content');
      
      if (response.data.success) {
        toast.success('Comment highlighted for moderation');
        setComments(prev => prev.map(comment => 
          comment.id === commentId 
            ? { ...comment, is_highlighted: 1 }
            : comment
        ));
      } else {
        toast.error(response.data.message || 'Failed to highlight comment');
      }
    } catch (error) {
      console.error('Error highlighting comment:', error);
      toast.error('Failed to highlight comment');
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!commentForm.content.trim()) {
      toast.error('Comment content is required');
      return;
    }

    try {
      setSubmitting(true);
      let decodedId = decodeForumId(id);
      if (!decodedId) {
        const numericId = parseInt(id);
        if (!isNaN(numericId)) {
          decodedId = numericId;
        } else {
          toast.error('Invalid forum ID');
          return;
        }
      }
      const response = await api.forums.createComment(decodedId, commentForm);
      
      if (response.data.success) {
        toast.success('Comment added successfully');
        setShowCommentModal(false);
        setCommentForm({ content: '', attachment: null });
        fetchComments();
      } else {
        toast.error(response.data.message || 'Failed to add comment');
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCommentForm(prev => ({ ...prev, attachment: file }));
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const canHighlightComment = () => {
    return user.role === 1 || user.role === 2; // Users and Org Admins can highlight
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid">
        <Alert variant="danger">
          {error}
        </Alert>
        <Button variant="outline-primary" onClick={() => navigate('/user/forums')}>
          <FaArrowLeft className="me-2" />
          Back to Forums
        </Button>
      </div>
    );
  }

  if (!forum) {
    return (
      <div className="container-fluid">
        <Alert variant="warning">
          Forum not found
        </Alert>
        <Button variant="outline-primary" onClick={() => navigate('/user/forums')}>
          <FaArrowLeft className="me-2" />
          Back to Forums
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 p-4 sm:p-6 md:p-8 lg:p-10">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
        <Col>
          <Row className='flex items-center gap-3'>
            <Col md={1}>
              <Button
              bsPrefix="custom-hamburger"
                variant="outline-secondary"
                onClick={() => navigate('/user/forums')}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-purple-300 text-purple-700 rounded-lg shadow-md hover:bg-purple-50 hover:shadow-lg transition-all duration-200 text-sm font-medium"
              >
                <FaArrowLeft />
                Back
              </Button>
            </Col>
            <Col md={6} className='text-start'>
              <h4 className="fw-bold mb-0 text-xl sm:text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">{forum.title}</h4>
            </Col>
            <Col md={4} className='text-end'>
              <Button variant="primary"  bsPrefix="custom-hamburger" size="sm" onClick={() => setShowCommentModal(true)} className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2 ">
                <i className="fas fa-plus me-2"></i>
                Add Comment
              </Button>
            </Col>
          </Row>
        </Col>
      </div>

      <Row>
        <Col lg={12}>
          {/* Forum Content */}
          <Card className="mb-4">
            {forum.attachment && (
              <Card.Img
                variant="top"
                src={getAssetUrl(forum.attachment)}
                style={{ maxHeight: '400px', objectFit: 'cover' }}
              />
            )}
            <Card.Body>
              <Card.Text className="mb-4" style={{ whiteSpace: 'pre-wrap' }}>
                {forum.description}
              </Card.Text>
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex gap-4">
                  <Button
                    variant={likedForums.has(forum.id) ? 'danger' : 'outline-danger'}
                    size="sm"
                    onClick={handleLikeForum}
                    className="d-flex align-items-center"
                  >
                    <FaHeart className="me-1" />
                    {forum.likes_count}
                  </Button>
                  <small className="text-muted d-flex align-items-center">
                    <i className="fas fa-comment me-1" />
                    {comments.length > 0 ? `${comments.length}\u00A0comments` : 'No comments'}
                  </small>
                  <small className="text-muted d-flex align-items-center">
                    <i className="fas fa-eye me-1" />
                    {`${forum.views_count || 0}\u00A0views`}
                  </small>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Comments Section */}
          <Card>
            <Card.Header>
              <h5 className="mb-0">
                {comments.length > 0 ? `Comments (${comments.length})` : 'Comments'}
              </h5>
            </Card.Header>
            <Card.Body>
              {commentsLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" size="sm" />
                  <span className="ms-2">Loading comments...</span>
                </div>
              ) : comments.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <FaComment size={32} className="mb-2" />
                  <p>No comments yet. Be the first to comment!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {comments.map(comment => (
                    <div key={comment.id} className="border-bottom pb-3 mb-3">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="d-flex align-items-center">
                          <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-2" 
                               style={{ width: '32px', height: '32px', fontSize: '14px' }}>
                            {comment.user?.first_name?.[0]}{comment.user?.last_name?.[0]}
                          </div>
                          <div>
                            <strong className="d-block">
                              {comment.user?.first_name} {comment.user?.last_name}
                            </strong>
                            <small className="text-muted">
                              {formatDate(comment.createdAt)}
                            </small>
                          </div>
                        </div>
                        {comment.is_highlighted !== 0 && (
                          <Badge bg="warning" className="d-flex align-items-center">
                            <FaFlag className="me-1" />
                            Flagged
                          </Badge>
                        )}
                      </div>
                      
                      <div className="ms-4">
                        <p className="mb-2" style={{ whiteSpace: 'pre-wrap' }}>
                          {comment.content}
                        </p>
                        
                        {comment.attachment && (
                          <div className="mb-2">
                            <Image
                              src={getAssetUrl(comment.attachment)}
                              alt="Comment attachment"
                              style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover' }}
                              className="rounded"
                            />
                          </div>
                        )}
                        
                        <div className="d-flex gap-3 align-items-center">
                          <Button
                                                  bsPrefix="custom-hamburger"
                            variant={likedComments.has(comment.id) ? 'primary' : 'outline-primary'}
                            size="sm"
                            onClick={() => handleLikeComment(comment.id)}
                            className="d-flex align-items-center"
                          >
                            <FaThumbsUp className="me-1" />
                            {comment.likes_count > 0 && (
                              <span className="ms-1">{comment.likes_count}</span>
                            )}
                          </Button>
                          
                          {canHighlightComment() && !comment.is_highlighted && (
                            <Button
                                                    bsPrefix="custom-hamburger"
                              variant="outline-warning"
                              size="sm"
                              onClick={() => handleHighlightComment(comment.id)}
                              className="d-flex align-items-center"
                            >
                              <FaFlag className="me-1" />
                              Flag as Vulgar
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Pagination for comments */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                  <nav>
                    <ul className="pagination">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                          disabled={currentPage === 1}
                        >
                          Previous
                        </button>
                      </li>
                      {[...Array(totalPages)].map((_, index) => (
                        <li
                          key={index + 1}
                          className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}
                        >
                          <button
                            className="page-link"
                            onClick={() => setCurrentPage(index + 1)}
                          >
                            {index + 1}
                          </button>
                        </li>
                      ))}
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                          disabled={currentPage === totalPages}
                        >
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
 
      {/* Add Comment Modal */}
      <Modal show={showCommentModal} onHide={() => setShowCommentModal(false)}>
        <Modal.Header closeButton closeVariant="white" className='bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 '>
          <Modal.Title>Add New Comment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmitComment} className='p-3'>
            <Form.Group className="mb-3">
              <Form.Label>Comment</Form.Label>
              <Form.Control
                type="text"
                placeholder="Write your comment here..."
                value={commentForm.content}
                onChange={(e) => setCommentForm({ ...commentForm, content: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Attachment (optional)</Form.Label>
              <Form.Control
                type="file"
                onChange={handleFileChange}
              />
            </Form.Group>
            <Button variant="primary" bsPrefix="custom-hamburger" className='w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2' type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    className="me-2"
                  />
                  Submitting...
                </>
              ) : (
                'Submit Comment'
              )}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default UserForumDetail;